<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ============================================================
  // Blocked users management page

  import { apiClient } from '../../utils/api';

  interface BlockedUser {
    id: number;
    blockedUser: {
      id: number;
      name: string;
      nickname?: string;
      avatarUrl?: string;
    };
    createdAt: string;
  }

  let blocks = $state<BlockedUser[]>([]);
  let isLoading = $state(true);
  let unblockingId = $state<number | null>(null);

  $effect(() => {
    loadBlocks();
  });

  async function loadBlocks() {
    isLoading = true;
    const res = await apiClient.get<{ blocks: BlockedUser[] }>('/api/v1/blocks');
    if (res && typeof res === 'object' && 'blocks' in res) {
      blocks = (res as { blocks: BlockedUser[] }).blocks;
    }
    isLoading = false;
  }

  async function unblock(blockId: number) {
    unblockingId = blockId;
    try {
      await apiClient.delete(`/api/v1/blocks/${blockId}`);
      blocks = blocks.filter(b => b.id !== blockId);
    } catch (err) {
      console.error('Unblock failed:', err);
    } finally {
      unblockingId = null;
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
</script>

<svelte:head>
  <title>차단 관리 | AX Admin</title>
</svelte:head>

<div class="space-y-6">
  <header class="bg-surface border-b border-default">
    <div class="py-6">
      <div class="flex items-center gap-4">
        <a href="/settings" aria-label="뒤로" class="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-high transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </a>
        <div>
          <h1 class="text-h1 text-content">차단 관리</h1>
          <p class="text-body2 text-content-tertiary mt-1">차단한 사용자를 관리합니다</p>
        </div>
      </div>
    </div>
  </header>

  <main class="py-8 max-w-2xl mx-auto">
    {#if isLoading}
      <div class="card p-12 text-center">
        <svg class="animate-spin w-10 h-10 mx-auto text-primary mb-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        <p class="text-body2 text-content-tertiary">로딩 중...</p>
      </div>
    {:else if blocks.length === 0}
      <div class="card p-12 text-center">
        <svg class="w-16 h-16 mx-auto text-gray-300 dark:text-dark-text-disabled mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
        <p class="text-h3 text-content mb-2">차단한 사용자 없음</p>
        <p class="text-body2 text-content-tertiary">현재 차단한 사용자가 없습니다.</p>
      </div>
    {:else}
      <div class="card overflow-hidden">
        <div class="p-4 border-b border-default bg-surface-tertiary">
          <p class="text-caption font-medium text-content-tertiary">
            총 {blocks.length}명의 사용자를 차단 중
          </p>
        </div>

        <div class="divide-y divide-gray-200 dark:divide-dark-border">
          {#each blocks as block}
            <div class="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center flex-shrink-0">
                  {#if block.blockedUser?.avatarUrl}
                    <img src={block.blockedUser.avatarUrl} alt="" class="w-full h-full rounded-xl object-cover" />
                  {:else}
                    <span class="text-sm font-bold text-white">
                      {block.blockedUser?.nickname?.charAt(0) || block.blockedUser?.name?.charAt(0) || '?'}
                    </span>
                  {/if}
                </div>
                <div>
                  <p class="text-body2 font-medium text-content">
                    {block.blockedUser?.nickname || block.blockedUser?.name || '알 수 없는 사용자'}
                  </p>
                  <p class="text-caption text-gray-400 dark:text-dark-text-disabled">
                    {formatDate(block.createdAt)}에 차단함
                  </p>
                </div>
              </div>

              <button
                onclick={() => unblock(block.id)}
                disabled={unblockingId === block.id}
                class="px-3 py-1.5 text-xs font-medium rounded-lg transition-all
                  bg-surface-tertiary text-content-secondary
                  border border-gray-300 dark:border-dark-border
                  hover:bg-green-50 hover:text-green-600 hover:border-green-200
                  dark:hover:bg-green-900/20 dark:hover:text-green-400
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {#if unblockingId === block.id}
                  <svg class="animate-spin w-3.5 h-3.5 inline-block" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                {:else}
                  차단 해제
                {/if}
              </button>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </main>
</div>
